// Enhanced database service - DatabaseManager wrapper for backward compatibility
import DatabaseManager from './DatabaseManager';
import { isSupabaseConfigured } from '../lib/supabaseClient';
import {
    signInWithEmail,
    signOutFromSupabase,
    getSupabaseSessionUser
} from './supabaseAuth';
import {
    fetchProperties,
    fetchPropertyById,
    createProperty,
    updatePropertyById,
    deletePropertyById,
    searchPropertiesSupabase
} from './supabaseProperties';
import { saveZipImportRecord, fetchZipImports } from './supabaseZipImports';

class DatabaseService {
    constructor() {
        this.manager = DatabaseManager;
        this.initializeData();
    }

    // Initialize default data
    initializeData() {
        if (isSupabaseConfigured) {
            return;
        }
        // Initialize default users if not exists (only for localStorage mode)
        if (this.manager.mode === 'localStorage' && !localStorage.getItem(this.manager.localStorageKeys.users)) {
            const defaultUsers = [
                { username: 'bigayrimenkul06', password: 'mutlakkazanc123', role: 'admin', name: 'Bi Gayrimenkul Admin' },
                { username: 'admin', password: 'admin123', role: 'admin', name: 'Admin Kullanıcı' }
            ];
            localStorage.setItem(this.manager.localStorageKeys.users, JSON.stringify(defaultUsers));
        }

        // Initialize empty properties if not exists
        if (this.manager.mode === 'localStorage' && !localStorage.getItem(this.manager.localStorageKeys.properties)) {
            localStorage.setItem(this.manager.localStorageKeys.properties, JSON.stringify([]));
        }
    }

    // ==========================================
    // USER AUTHENTICATION - Backward Compatibility
    // ==========================================

    async authenticateUser(usernameOrEmail, password) {
        try {
            if (isSupabaseConfigured) {
                return await signInWithEmail(usernameOrEmail, password);
            }
            return await this.manager.authenticateUser(usernameOrEmail, password);
        } catch (error) {
            console.error('Authentication error:', error);
            const message = error?.message || '';
            if (/failed to fetch|network|err_name_not_resolved/i.test(message)) {
                return {
                    error: 'Sunucuya bağlanılamadı. Netlify Supabase URL ayarını kontrol edin (boşluksuz https://PROJE_ID.supabase.co).'
                };
            }
            return { error: message || 'Giriş sırasında bir hata oluştu.' };
        }
    }

    async getUsers() {
        return await this.manager.getUsers();
    }

    async getCurrentUser() {
        if (isSupabaseConfigured) {
            return await getSupabaseSessionUser();
        }
        const token = this.manager.getToken();
        return token ? { token, username: token.split('_')[2] || 'user' } : null;
    }

    async logout() {
        if (isSupabaseConfigured) {
            await signOutFromSupabase();
        }
        localStorage.removeItem('auth_token');
        this.manager.setToken('');
    }

    // ==========================================
    // PROPERTY MANAGEMENT - Backward Compatibility
    // ==========================================

    async getAllProperties() {
        if (isSupabaseConfigured) {
            return await fetchProperties({ includeUnpublished: false });
        }
        return await this.manager.getProperties();
    }

    async getProperties() {
        if (isSupabaseConfigured) {
            return await fetchProperties({ includeUnpublished: true });
        }
        return await this.manager.getProperties();
    }

    async addProperty(property) {
        try {
            if (isSupabaseConfigured) {
                return await createProperty(property);
            }
            return await this.manager.addProperty(property);
        } catch (error) {
            console.error('Add property error:', error);
            const message = error?.message || error?.details || 'Emlak eklenirken bir hata oluştu.';
            throw new Error(message);
        }
    }

    async updateProperty(id, updatedProperty) {
        try {
            if (isSupabaseConfigured) {
                return await updatePropertyById(id, updatedProperty);
            }
            return await this.manager.updateProperty(id, updatedProperty);
        } catch (error) {
            console.error('Update property error:', error);
            throw new Error('Emlak güncellenirken bir hata oluştu.');
        }
    }

    async deleteProperty(id) {
        try {
            if (isSupabaseConfigured) {
                return await deletePropertyById(id);
            }
            return await this.manager.deleteProperty(id);
        } catch (error) {
            console.error('Delete property error:', error);
            throw new Error('Emlak silinirken bir hata oluştu.');
        }
    }

    // ==========================================
    // IMAGE MANAGEMENT - Backward Compatibility
    // ==========================================

    uploadImage(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                resolve(event.target.result);
            };
            reader.onerror = (error) => {
                reject(error);
            };
            reader.readAsDataURL(file);
        });
    }

    async uploadImages(propertyId, files) {
        const imagePromises = files.map(file => this.uploadImage(file));
        return await Promise.all(imagePromises);
    }

    // ==========================================
    // NEW METHODS - DatabaseManager Features
    // ==========================================

    async searchProperties(filters) {
        if (isSupabaseConfigured) {
            return await searchPropertiesSupabase(filters);
        }
        return await this.manager.searchProperties(filters);
    }

    async getPropertyById(id) {
        if (isSupabaseConfigured) {
            return await fetchPropertyById(id);
        }
        return await this.manager.getPropertyById(id);
    }

    async saveZipImport(zipImportData) {
        if (isSupabaseConfigured) {
            return await saveZipImportRecord(zipImportData);
        }
        return await this.manager.saveZipImport(zipImportData);
    }

    async getZipImports() {
        if (isSupabaseConfigured) {
            return await fetchZipImports();
        }
        return await this.manager.getZipImports();
    }

    async getStatistics() {
        return await this.manager.getStatistics();
    }

    async healthCheck() {
        return await this.manager.healthCheck();
    }

    // Database mode switching
    setDatabaseMode(mode) {
        this.manager.setMode(mode);
    }

    getDatabaseMode() {
        return this.manager.mode;
    }

    async syncToAPI() {
        return await this.manager.syncToAPI();
    }

    // ==========================================
    // SETTINGS MANAGEMENT
    // ==========================================

    getSettings() {
        if (this.manager.mode === 'localStorage') {
            return JSON.parse(localStorage.getItem(this.manager.localStorageKeys.settings) || '{}');
        }
        // API mode için gelecekte implement edilecek
        return {};
    }

    saveSettings(settings) {
        if (this.manager.mode === 'localStorage') {
            localStorage.setItem(this.manager.localStorageKeys.settings, JSON.stringify(settings));
        }
        // API mode için gelecekte implement edilecek
    }
}

// Export singleton instance
const databaseService = new DatabaseService();
export default databaseService;
